import { Purchases } from "@revenuecat/purchases-capacitor";
import { useEffect, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { useSelector } from "react-redux";

const useRevenueCat = () => {
  const { activeUser } = useSelector((state) => state.user);
  const isNative = Capacitor.isNativePlatform();
  useEffect(() => {
    if (!isNative || !activeUser) {
      return;
    }
    Purchases.configure({
      apiKey: "goog_PYTupLpnDvDJHfaKgADwaOjGelv", // RevenueCat Android public key
      appUserID: activeUser.userId,
    });
  }, [activeUser, isNative]);

  async function purchaseMonthlySubscription() {
    try {
      const offerings = await Purchases.getOfferings();

      if (
        !offerings.current ||
        offerings.current.availablePackages.length === 0
      ) {
        console.warn("No offerings available");
        return { success: false, cancelled: false };
      }

      const subscriptionPackage = offerings.current.availablePackages[0];

      const { customerInfo } = await Purchases.purchasePackage({
        aPackage: subscriptionPackage,
      });

      const isSubscribed = customerInfo.activeSubscriptions.includes(
        "mindstack_subscription_monthly",
      );

      if (isSubscribed) {
        console.log("Purchase successful, user is subscribed");
        return { success: true, cancelled: false };
      } else {
        console.log("Purchase completed but user is not subscribed");
        return { success: false, cancelled: false };
      }
    } catch (e) {
      if (e.userCancelled) {
        console.log("User cancelled purchase");
        return { success: false, cancelled: true };
      } else {
        console.error("Purchase failed:", e);
        return { success: false, cancelled: false };
      }
    }
  }

  function openSubscriptionManagement() {
    // Deep link to Google Play subscription management
    // This opens the Play Store subscription management page for this app
    const packageName = "app.mindstack";
    const playStoreUrl = `https://play.google.com/store/account/subscriptions?package=${packageName}`;
    window.open(playStoreUrl, "_system");
  }

  /**
   * Fetches subscription status directly from RevenueCat.
   * This is useful because Google Play Store doesn't always send cancellation
   * notifications to your backend immediately - it may wait until the subscription
   * period ends. RevenueCat, however, can detect cancellation state from the Play Store
   * in real-time when queried.
   *
   * Returns:
   * - isActive: whether the subscription is currently active (has entitlement)
   * - willRenew: whether the subscription will renew (false if cancelled)
   * - expirationDate: when the subscription expires (ISO string or null)
   */
  const getSubscriptionInfo = useCallback(async () => {
    if (!isNative) {
      return null;
    }

    try {
      const { customerInfo } = await Purchases.getCustomerInfo();

      const entitlement =
        customerInfo.entitlements?.active?.["mindstack_member"];
      const isActive = !!entitlement;

      if (!isActive) {
        return {
          isActive: false,
          willRenew: false,
          expirationDate: null,
        };
      }

      // Check if the subscription will renew
      // willRenew is false if the user has cancelled (subscription ends at expirationDate)
      const willRenew = entitlement.willRenew ?? true;
      const expirationDate = entitlement.expirationDate ?? null;

      return {
        isActive,
        willRenew,
        expirationDate,
      };
    } catch (e) {
      console.error("Failed to get RevenueCat subscription info:", e);
      return null;
    }
  }, [isNative]);

  return {
    purchaseMonthlySubscription,
    openSubscriptionManagement,
    getSubscriptionInfo,
    isNative,
  };
};

export default useRevenueCat;
